import React, { useState, useMemo } from "react"
import { Link } from "react-router-dom"
import moment from "moment"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import CatchTable from "~/library/CatchTable"
import Pagination from "~/library/Pagination"
import ButtonGroup from "~/library/ButtonGroup"
import NavigationLink from "~/library/NavigationLink"

import getAppConfig from "~/utils/getAppConfig"
import { useToast } from "~/utils/Toast"
import useFetchResource from "~/hooks/useFetchResource"
import { formatCurrency, titleize } from "~/utils/helpers"
import { apiWorkspacePayments } from "~/routes/"
import type Payment from "~/types/Payment"

const paramifyQ = (q?: Record<string, string | number | null | undefined>) =>
  Object.fromEntries(
    Object.entries(q || {})
      .filter(([, v]) => v != null && v !== "")
      .map(([k, v]) => [`q[${k}]`, String(v)])
  )

const buildRansackQuery = (filters: {
  scope: string
  paymentType: string
  status: string
  type: string
  search: string
}) => {
  const q: Record<string, string> = {}

  if (filters.scope === "booking") q["payable_type_eq"] = "Booking"
  else if (filters.scope === "deal") q["payable_type_eq"] = "Deal"
  else if (filters.scope === "sponsorship") q["payable_type_eq"] = "Sponsorship"

  if (filters.paymentType) q["payment_method_eq"] = filters.paymentType
  if (filters.status) q["status_eq"] = filters.status
  if (filters.type) q["type_eq"] = filters.type

  if (filters.search && !isNaN(Number(filters.search))) {
    q["amount_cents_eq"] = String(Number(filters.search) * 100)
  }

  return q
}

const PaymentList = () => {
  const { user, venueSlug } = getAppConfig()
  const toast = useToast()
  const [filters, setFilters] = useState({
    scope: "all",
    paymentType: "",
    status: "",
    type: "",
    search: "",
  })

  const isNumericSearch = filters.search.trim() !== "" && !isNaN(Number(filters.search))

  const {
    data: payments,
    refetch,
    metadata,
    isLoading,
  } = useFetchResource<{ payments: Payment[] }, "payments">(
    apiWorkspacePayments.index.path({ venueSlug }),
    "payments",
    {
      page: "1",
      ...(isNumericSearch ? {} : { search: filters.search }),
      ...paramifyQ(buildRansackQuery(filters)),
    }
  )

  const handleFilterChange = (key: keyof typeof filters, value: string) => {
    const updated = { ...filters, [key]: value }
    const numericSearch = updated.search.trim() !== "" && !isNaN(Number(updated.search))

    setFilters(updated)

    refetch({
      page: "1",
      ...(numericSearch ? {} : { search: updated.search }),
      ...paramifyQ(buildRansackQuery(updated)),
    })
  }

  const handlePageChange = (page: string) => {
    const numericSearch = filters.search.trim() !== "" && !isNaN(Number(filters.search))

    refetch({
      page,
      ...(numericSearch ? {} : { search: filters.search }),
      ...paramifyQ(buildRansackQuery(filters)),
    })
  }

  const columns = useMemo(() => {
    const copyLink = async (link?: string | null) => {
      if (!link) return

      try {
        await navigator.clipboard.writeText(link)
        toast("Link copied", "success")
      } catch (_error) {
        toast("Failed to copy link", "danger")
      }
    }

    const base = [
      {
        key: "id",
        header: "Payment ID",
        formatter: (_id, row) => (
          <NavigationLink type="payments" to={`/payments/${row.id}`}>
            INV-{(row.id || "").slice(0, 6).toUpperCase()}
          </NavigationLink>
        ),
      },

      {
        key: "payableType",
        header: "Payment Source",
        formatter: (_v, row) => row.payableType ?? "—",
      },

      {
        key: "type",
        header: "Creation Source",
        formatter: (value: string) => titleize(value),
      },

      {
        key: "issueDate",
        header: "Issue Date",
        formatter: (issueDate) => (issueDate ? moment(issueDate).format("MMM Do YYYY") : "—"),
      },
      {
        key: "customer",
        header: "Customer",
        formatter: (_v, row) => <div>{row.customer?.name ?? "—"}</div>,
      },
      {
        key: "payment_method",
        header: "Payment Method",
        formatter: (value: string) => titleize(value),
      },
      {
        key: "status",
        header: "Status",
        formatter: (value: string) => {
          const label = value ? value.charAt(0).toUpperCase() + value.slice(1) : "—"
          const badgeClass =
            {
              draft: "badge bg-secondary",
              pending: "badge bg-warning",
              overdue: "badge bg-danger",
              paid: "badge bg-success",
            }[value] || "badge bg-light text-dark"

          return <span className={badgeClass}>{label}</span>
        },
      },
      {
        key: "amount",
        header: "Total",
        formatter: (amount) =>
          formatCurrency({
            cents: amount.cents,
            currency_iso: amount.currency_iso,
          }),
      },
      {
        key: "processingFeeCents",
        header: "Processing Fee",
        formatter: (feeCents: number, row: Payment) =>
          formatCurrency({
            cents: feeCents || 0,
            currency_iso: row.amount.currency_iso,
          }),
      },
      {
        key: "receiptUrl",
        header: "Link",
        formatter: (receiptUrl: string | null, payment: Payment) => {
          const link = payment.hostedInvoiceUrl || receiptUrl
          const label = payment.hostedInvoiceUrl ? "Invoice" : "Copy link"

          return link ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => copyLink(link)}
            >
              {label}
            </button>
          ) : (
            "—"
          )
        },
      },
    ]

    if (filters.scope !== "all") {
      return base.filter((c) => c.key !== "payableType")
    }

    return base
  }, [filters.scope, toast])

  return (
    <LoadingWrapper isLoading={isLoading}>
      <PageTitle title="Payments">
        {user.role === "internal" && (
          <Link className="btn btn-outline-primary" to="/payments/payouts">
            Payouts
          </Link>
        )}
      </PageTitle>

      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-center mb-3 gap-3 flex-wrap">
            <ButtonGroup
              options={[
                { label: "All", value: "all" },
                { label: "Booking", value: "booking" },
                { label: "Deal", value: "deal" },
                { label: "Sponsorship", value: "sponsorship" },
              ]}
              onClick={(value) => handleFilterChange("scope", value)}
              selectedValue={filters.scope}
            />
            <div className="d-flex gap-2 ms-auto flex-wrap align-items-center">
              <select
                className="form-select w-auto"
                value={filters.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
              >
                <option value="">All Sources</option>
                <option value="Invoice">Invoice</option>
                <option value="Manual">Manual</option>
                <option value="SavedCardCharge">Saved Card Charge</option>
                <option value="InstantPayment">Instant Payment</option>
              </select>

              <select
                className="form-select w-auto"
                value={filters.paymentType}
                onChange={(e) => handleFilterChange("paymentType", e.target.value)}
              >
                <option value="">All Types</option>
                <option value="saved_card">Saved Card</option>
                <option value="card">Card</option>
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="paypal">PayPal</option>
                <option value="other">Other</option>
              </select>

              <select
                className="form-select w-auto"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
                <option value="paid">Paid</option>
              </select>

              <input
                type="text"
                className="form-control w-auto"
                placeholder="Search"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>
          </div>

          <CatchCard className="card-dark-theme">
            <CatchTable
              key={`${filters.scope}-${filters.paymentType}-${filters.status}-${filters.type}-${filters.search}`}
              data={payments}
              emptyMessage="No Payments"
              columns={columns}
            />
            {metadata && (
              <Pagination
                metadata={metadata}
                onPageChange={(page: number) => handlePageChange(page.toString())}
              />
            )}
          </CatchCard>
        </div>
      </div>
    </LoadingWrapper>
  )
}

export default PaymentList
